/**
 * FERP Portal - Regulatory Rules Review Page (DRR-058)
 *
 * LIFE-CRITICAL: Regulatory Intelligence Pipeline review dashboard.
 * LLM-extracted rules are routed to confidence-based queues for
 * human review before enforcement via PolicyRuleRepository.
 *
 * Pattern sources:
 * - FormSchemaAdminPage (tabs)
 * - WorkflowListPage (table with safety badges)
 * - JournalEntryApprovePage (approve/reject actions)
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Table,
  Tabs,
  Progress,
  SimpleGrid,
  Loader,
  Button,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconShieldCheck,
  IconInfoCircle,
  IconAlertTriangle,
  IconFileImport,
  IconCheck,
  IconX,
  IconClockHour4,
  IconRobot,
  IconScale,
  IconEye,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListRegulatoryRulesQuery,
  useGetRegulatoryQueueStatsQuery,
  GqlRuleStatus,
  GqlReviewQueue,
  GqlSafetyLevel,
  type RegulatoryRuleFieldsFragment,
} from '@/generated/graphql';
import { RuleImportModal } from './RuleImportModal';
import { RuleDetailDrawer } from './RuleDetailDrawer';
import {
  SAFETY_BADGE_CONFIG,
  RULE_STATUS_BADGE_CONFIG as STATUS_BADGE_CONFIG,
  REVIEW_QUEUE_BADGE_CONFIG as QUEUE_BADGE_CONFIG,
} from '@/config/regulatory-status';

type TabValue = 'all' | 'safety' | 'conflict' | 'confidence' | 'auto';

const TAB_TO_QUEUE: Record<Exclude<TabValue, 'all'>, GqlReviewQueue> = {
  safety: GqlReviewQueue.SAFETY,
  conflict: GqlReviewQueue.CONFLICT,
  confidence: GqlReviewQueue.CONFIDENCE,
  auto: GqlReviewQueue.AUTO,
};

// ============================================================================
// Confidence color helper
// ============================================================================

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'green';
  if (confidence >= 0.7) return 'yellow';
  return 'red';
}

// ============================================================================
// Component
// ============================================================================

export function RegulatoryRulesPage() {
  const tenantId = useTenantId();
  const [activeTab, setActiveTab] = useState<TabValue>('all');
  const [selectedRule, setSelectedRule] = useState<RegulatoryRuleFieldsFragment | null>(null);
  const [importModalOpened, { open: openImport, close: closeImport }] = useDisclosure(false);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const skip = !tenantId;

  // Build filter based on active tab
  const queueFilter = activeTab !== 'all' ? TAB_TO_QUEUE[activeTab] : undefined;

  const {
    data: rulesData,
    loading: rulesLoading,
    refetch: refetchRules,
  } = useListRegulatoryRulesQuery({
    variables: {
      filter: {
        tenantId: tenantId ?? '',
        ...(queueFilter ? { queue: queueFilter } : {}),
        limit: 100,
      },
    },
    skip,
  });

  const {
    data: statsData,
    loading: statsLoading,
  } = useGetRegulatoryQueueStatsQuery({
    variables: { tenantId: tenantId ?? '' },
    skip,
  });

  const rules = useMemo(() => rulesData?.regulatoryRules ?? [], [rulesData]);
  const stats = useMemo(() => statsData?.regulatoryQueueStats ?? null, [statsData]);

  const loading = rulesLoading || statsLoading;

  const handleRowClick = useCallback(
    (rule: RegulatoryRuleFieldsFragment) => {
      setSelectedRule(rule);
      openDrawer();
    },
    [openDrawer],
  );

  const handleDrawerClose = useCallback(() => {
    closeDrawer();
    setSelectedRule(null);
  }, [closeDrawer]);

  const handleActionComplete = useCallback(() => {
    refetchRules();
  }, [refetchRules]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconInfoCircle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view regulatory rules.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Regulatory Rules</Title>
            <Text c="dimmed" size="sm">
              LLM-extracted rules routed through confidence-based human review
            </Text>
          </div>
          <Button leftSection={<IconFileImport size={16} />} onClick={openImport}>
            Import Rules
          </Button>
        </Group>

        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="blue" size="lg">
                <IconScale size={20} />
              </ThemeIcon>
              <div>
                <Text size="xl" fw={700}>{stats?.total ?? 0}</Text>
                <Text size="xs" c="dimmed">Total Rules</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="red" size="lg">
                <IconShieldCheck size={20} />
              </ThemeIcon>
              <div>
                <Text size="xl" fw={700}>{stats?.safetyReview ?? 0}</Text>
                <Text size="xs" c="dimmed">Safety Review</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="orange" size="lg">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
              <div>
                <Text size="xl" fw={700}>{stats?.conflictReview ?? 0}</Text>
                <Text size="xs" c="dimmed">Conflicts</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="yellow" size="lg">
                <IconClockHour4 size={20} />
              </ThemeIcon>
              <div>
                <Text size="xl" fw={700}>{stats?.confidenceReview ?? 0}</Text>
                <Text size="xs" c="dimmed">Review</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="green" size="lg">
                <IconRobot size={20} />
              </ThemeIcon>
              <div>
                <Text size="xl" fw={700}>{stats?.autoApproved ?? 0}</Text>
                <Text size="xs" c="dimmed">Auto-Approved</Text>
              </div>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Queue Tabs + Table */}
        <Card withBorder p="md">
          <Tabs value={activeTab} onChange={(v) => setActiveTab((v ?? 'all') as TabValue)}>
            <Tabs.List>
              <Tabs.Tab value="all" leftSection={<IconEye size={14} />}>
                All Queues
                <Badge size="xs" variant="light" ml={4}>{stats?.total ?? 0}</Badge>
              </Tabs.Tab>
              <Tabs.Tab value="safety" leftSection={<IconShieldCheck size={14} />}>
                Safety
                <Badge size="xs" variant="light" color="red" ml={4}>{stats?.safetyReview ?? 0}</Badge>
              </Tabs.Tab>
              <Tabs.Tab value="conflict" leftSection={<IconAlertTriangle size={14} />}>
                Conflicts
                <Badge size="xs" variant="light" color="orange" ml={4}>{stats?.conflictReview ?? 0}</Badge>
              </Tabs.Tab>
              <Tabs.Tab value="confidence" leftSection={<IconClockHour4 size={14} />}>
                Review
                <Badge size="xs" variant="light" color="yellow" ml={4}>{stats?.confidenceReview ?? 0}</Badge>
              </Tabs.Tab>
              <Tabs.Tab value="auto" leftSection={<IconRobot size={14} />}>
                Auto-Approved
                <Badge size="xs" variant="light" color="green" ml={4}>{stats?.autoApproved ?? 0}</Badge>
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value={activeTab} pt="md">
              {loading ? (
                <Group justify="center" py="xl">
                  <Loader size="lg" />
                </Group>
              ) : rules.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No rules in this queue.
                </Text>
              ) : (
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Rule ID</Table.Th>
                      <Table.Th>Actor</Table.Th>
                      <Table.Th>Action</Table.Th>
                      <Table.Th>Deadline</Table.Th>
                      <Table.Th>Confidence</Table.Th>
                      <Table.Th>Safety</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Queue</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rules.map((rule) => {
                      const safetyConfig = SAFETY_BADGE_CONFIG[rule.classification.safetyLevel] ?? { color: 'gray', label: rule.classification.safetyLevel };
                      const statusConfig = STATUS_BADGE_CONFIG[rule.status] ?? { color: 'gray', label: rule.status };
                      const queueConfig = QUEUE_BADGE_CONFIG[rule.queue] ?? { color: 'gray', label: rule.queue };
                      const isCritical = rule.classification.safetyLevel === GqlSafetyLevel.CRITICAL;

                      return (
                        <Table.Tr
                          key={`${rule.id}-v${rule.version}`}
                          onClick={() => handleRowClick(rule)}
                          style={{
                            cursor: 'pointer',
                            borderLeft: isCritical ? '3px solid var(--mantine-color-red-6)' : undefined,
                          }}
                        >
                          <Table.Td>
                            <Text size="sm" fw={500}>{rule.id}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{rule.structured.actor}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{rule.structured.action}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{rule.structured.deadline ?? '-'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs" wrap="nowrap">
                              <Progress
                                value={rule.extraction.confidence * 100}
                                color={getConfidenceColor(rule.extraction.confidence)}
                                size="sm"
                                w={60}
                              />
                              <Text size="xs" c="dimmed">
                                {Math.round(rule.extraction.confidence * 100)}%
                              </Text>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={safetyConfig.color}
                              variant="light"
                              size="sm"
                            >
                              {safetyConfig.label}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={statusConfig.color}
                              variant="light"
                              size="sm"
                              leftSection={
                                rule.status === GqlRuleStatus.APPROVED ? <IconCheck size={10} /> :
                                rule.status === GqlRuleStatus.REJECTED ? <IconX size={10} /> :
                                undefined
                              }
                            >
                              {statusConfig.label}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={queueConfig.color}
                              variant="outline"
                              size="xs"
                            >
                              {queueConfig.label}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Tabs.Panel>
          </Tabs>
        </Card>
      </Stack>

      {/* Import Modal */}
      <RuleImportModal
        opened={importModalOpened}
        onClose={closeImport}
        tenantId={tenantId}
        onImportComplete={handleActionComplete}
      />

      {/* Detail Drawer */}
      <RuleDetailDrawer
        opened={drawerOpened}
        onClose={handleDrawerClose}
        rule={selectedRule}
        tenantId={tenantId}
        onActionComplete={handleActionComplete}
      />
    </Container>
  );
}

export default RegulatoryRulesPage;
